import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { API_BASE } from '@/constants/api';
import { setPreferredPortal } from '@/utils/portalRouting';

function handoffCodeFromLocation(): string | null {
  return new URLSearchParams(window.location.search).get('handoff');
}

function pathWithoutHandoff(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete('handoff');
  return `${url.pathname}${url.search}${url.hash}`;
}

function isSafeNext(path: string): boolean {
  return /^\/installer\/surveys\/\d+$/.test(path);
}

/**
 * Dashboard → SPA identity bridge. Must run before PortalGate so a customer
 * session in localStorage cannot send the admin to /portal.
 *
 * Uses ?handoff= on an existing SPA path. Production powlo.co returns HTTP 404
 * for /auth/* (that prefix is not the React app).
 */
export default function SpaHandoff({ children }: { children: ReactNode }) {
  const [code] = useState(handoffCodeFromLocation);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;

    let cancelled = false;
    void (async () => {
      try {
        await fetch(`${API_BASE}/sanctum/csrf-cookie`, {
          method: 'GET',
          credentials: 'include',
        });
        const response = await fetch(`${API_BASE}/api/auth/spa-handoff`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });
        const data = (await response.json().catch(() => ({}))) as {
          success?: boolean;
          message?: string;
          data?: { token?: string; user?: unknown; next?: string };
        };
        if (cancelled) return;
        if (!response.ok || !data.success || !data.data?.token || !data.data.user) {
          setError(data.message || 'Could not open the survey. Try again from the dashboard.');
          return;
        }
        localStorage.setItem('auth_token', data.data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.data.user));
        setPreferredPortal('installer');
        const next = data.data.next;
        window.location.replace(
          typeof next === 'string' && isSafeNext(next) ? next : pathWithoutHandoff(),
        );
      } catch {
        if (!cancelled) {
          setError('Network error. Try again from the dashboard.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!code) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-[#050d1a] text-white flex items-center justify-center px-6">
      {error ? (
        <p className="text-sm text-red-400">{error}</p>
      ) : (
        <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
      )}
    </div>
  );
}
